/**
 * Patient-facing terminology helper
 *
 * Business context (plain English):
 * - Most organisations are comfortable with the word "Informant" to describe a person who
 *   provides collateral information about a patient.
 * - The SLaM customer ("3 Borough Adult ADHD and Autism Service" / "SLaM") prefers
 *   “Key Contact(s)” instead, due to negative connotations with "Informant".
 *
 * Design goals:
 * - Centralise the copy choice in one place so all patient-facing components can depend on
 *   a single source of truth and we avoid hard-coded strings scattered across the app.
 * - Remain fully backward-compatible for other organisations (default remains “Informant(s)”).
 * - Be easy to extend for future customers with different terminology preferences.
 *
 * How to use:
 *   import { getPatientFacingTerminology } from '@/utils/terminology';
 *
 *   const { singular, plural, lowerSingular, lowerPlural } =
 *     getPatientFacingTerminology(userOrganisation);
 *
 *   // Example usages in UI copy:
 *   // - Button: `Manage ${plural}` → "Manage Informants" or "Manage Key Contacts"
 *   // - Sentence: `set up your ${lowerPlural}` → "set up your informants" or "set up your key contacts"
 *
 * Notes:
 * - This helper only affects patient-facing copy. Provider-facing copy should continue using
 *   the default “Informant(s)” wording unless explicitly required otherwise.
 * - Internal identifiers (Firestore fields, auth claims like userType === 'Informant',
 *   form keys such as 'baarsIVCurrentInformant', etc.) must not be changed, as they are
 *   part of the data model and routing logic.
 */

/**
 * Normalises an organisation string for robust matching.
 */
function normaliseOrganisationName(organisation) {
  if (!organisation || typeof organisation !== 'string') return '';
  return organisation.trim().toLowerCase();
}

/**
 * Returns patient-facing terminology (singular/plural, lower/upper case forms)
 * based on the organisation. Defaults to "Informant(s)".
 *
 * For SLaM (aliases supported: "3 Borough Adult ADHD and Autism Service", "SLaM"):
 *   - "Key Contact" / "Key Contacts"
 */
export function getPatientFacingTerminology(organisation) {
  const org = normaliseOrganisationName(organisation);

  // Known SLaM identifiers (add more aliases if needed later)
  const isSLaM =
    org === '3 borough adult adhd and autism service' ||
    org === 'slam';

  if (isSLaM) {
    return {
      singular: 'Key Contact',
      plural: 'Key Contacts',
      lowerSingular: 'key contact',
      lowerPlural: 'key contacts',
    };
  }

  // Default terminology
  return {
    singular: 'Informant',
    plural: 'Informants',
    lowerSingular: 'informant',
    lowerPlural: 'informants',
  };
}

export default getPatientFacingTerminology;


